import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Trash2, ToggleLeft, ToggleRight, Eye, Pencil, Save, X, Tag as TagIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface HeadlineVariantRowProps {
  variant: {
    id: string;
    headline_text: string;
    subtitle: string | null;
    button_text: string | null;
    is_active: boolean;
    notes: string | null;
    tags: string[] | null;
    impressions: number;
    conversions: number;
    conversion_rate: number;
    quiz_only_conversions: number;
    quiz_only_conversion_rate: number;
    lead_conversions: number;
    lead_conversion_rate: number;
    cta_clicks: number;
    cta_click_rate: number;
  };
  onToggleActive: (id: string, currentStatus: boolean) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string, data: { headline_text: string; subtitle: string | null; button_text: string | null; notes: string; tags: string[] }) => Promise<void>;
}

export const HeadlineVariantRow = ({ variant, onToggleActive, onDelete, onEdit }: HeadlineVariantRowProps) => {
  const { toast } = useToast();
  const [showPreview, setShowPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Edit form state - synchronized with variant
  const [editHeadline, setEditHeadline] = useState(variant.headline_text);
  const [editSubtitle, setEditSubtitle] = useState(variant.subtitle || '');
  const [editButtonText, setEditButtonText] = useState(variant.button_text || '');
  const [editNotes, setEditNotes] = useState(variant.notes || '');
  const [editTags, setEditTags] = useState<string[]>(variant.tags || []);
  const [tagInput, setTagInput] = useState("");
  
  // Inline tag editing state
  const [isEditingTagsInline, setIsEditingTagsInline] = useState(false);
  const [inlineTags, setInlineTags] = useState<string[]>(variant.tags || []);
  const [inlineTagInput, setInlineTagInput] = useState("");
  
  const shortId = variant.id.substring(0, 8);
  
  // Generate campaign URLs in different formats
  const tags = variant.tags || [];
  const primaryTag = tags[0];
  
  const utmContentUrl = primaryTag 
    ? `${window.location.origin}/?utm_content=${encodeURIComponent(primaryTag)}`
    : null;
  const pathBasedUrl = primaryTag 
    ? `${window.location.origin}/c/${encodeURIComponent(primaryTag)}`
    : null;
  const legacyTagUrl = primaryTag 
    ? `${window.location.origin}/?tag=${encodeURIComponent(primaryTag)}`
    : null;
  const variantUrl = `${window.location.origin}/?v=${shortId}`;
  
  // Full Meta Ads UTM template
  const fullMetaUtm = primaryTag
    ? `${window.location.origin}/?utm_source=facebook&utm_medium=paid&utm_content=${encodeURIComponent(primaryTag)}&utm_campaign={{campaign.name}}`
    : null;

  // Sync form values with variant changes
  useEffect(() => {
    setEditHeadline(variant.headline_text);
    setEditSubtitle(variant.subtitle || '');
    setEditButtonText(variant.button_text || '');
    setEditNotes(variant.notes || '');
    setEditTags(variant.tags || []);
    setInlineTags(variant.tags || []);
  }, [variant]);

  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !editTags.includes(trimmedTag)) {
      setEditTags([...editTags, trimmedTag]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };

  const addInlineTag = () => {
    const trimmedTag = inlineTagInput.trim();
    if (trimmedTag && !inlineTags.includes(trimmedTag)) {
      setInlineTags([...inlineTags, trimmedTag]);
      setInlineTagInput("");
    }
  };

  const removeInlineTag = (tagToRemove: string) => {
    setInlineTags(inlineTags.filter(tag => tag !== tagToRemove));
  };

  const handleSaveInlineTags = async () => {
    try {
      await onEdit(variant.id, {
        headline_text: variant.headline_text,
        subtitle: variant.subtitle,
        button_text: variant.button_text,
        notes: variant.notes || '',
        tags: inlineTags
      });
      
      toast({
        title: "Sukces",
        description: "Tagi zostały zaktualizowane",
      });
      
      setIsEditingTagsInline(false);
    } catch (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się zaktualizować tagów",
        variant: "destructive",
      });
    }
  };

  const handleCopyLink = async (url: string, type: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link skopiowany",
        description: `Link ${type} został skopiowany do schowka`,
      });
    } catch (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się skopiować linku",
        variant: "destructive",
      });
    }
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    // Values are already synced via useEffect
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset to current variant values on cancel
    setEditHeadline(variant.headline_text);
    setEditSubtitle(variant.subtitle || '');
    setEditButtonText(variant.button_text || '');
    setEditNotes(variant.notes || '');
    setEditTags(variant.tags || []);
    setTagInput("");
  };

  const handleSaveEdit = async () => {
    if (!editHeadline.trim()) {
      toast({
        title: "Błąd",
        description: "Nagłówek nie może być pusty",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      await onEdit(variant.id, {
        headline_text: editHeadline,
        subtitle: editSubtitle.trim() || null,
        button_text: editButtonText.trim() || null,
        notes: editNotes.trim(),
        tags: editTags
      });
      
      toast({
        title: "Sukces",
        description: "Wariant został zaktualizowany",
      });
      
      setIsEditing(false);
    } catch (error) {
      toast({
        title: "Błąd",
        description: "Nie udało się zaktualizować wariantu",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
    <TableRow>
      <TableCell className="max-w-md">
        <div className="space-y-1">
          <div className="font-mono text-[10px] text-muted-foreground/70 mb-0.5">
            ID: {shortId}
          </div>
          <div className="font-medium line-clamp-2">{variant.headline_text}</div>
          {variant.notes && (
            <div className="text-xs text-muted-foreground mt-1">{variant.notes}</div>
          )}
        </div>
      </TableCell>
      <TableCell className="max-w-xs">
        <Popover open={isEditingTagsInline} onOpenChange={setIsEditingTagsInline}>
          <PopoverTrigger asChild>
            <div className="cursor-pointer">
              {inlineTags && inlineTags.length > 0 ? (
                <div className="flex gap-1 flex-wrap">
                  {inlineTags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : (
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground">
                  <TagIcon className="h-3 w-3 mr-1" />
                  Dodaj tag
                </Button>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Tagi kampanii</Label>
                <div className="flex gap-2">
                  <Input
                    value={inlineTagInput}
                    onChange={(e) => setInlineTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addInlineTag();
                      }
                    }}
                    placeholder="np. rachunkowość"
                    className="h-8 text-sm"
                  />
                  <Button type="button" onClick={addInlineTag} variant="outline" size="sm" className="h-8">
                    Dodaj
                  </Button>
                </div>
                {inlineTags.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {inlineTags.map(tag => (
                      <Badge key={tag} variant="secondary" className="gap-1 text-xs">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeInlineTag(tag)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setInlineTags(variant.tags || []);
                    setInlineTagInput("");
                    setIsEditingTagsInline(false);
                  }}
                >
                  Anuluj
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveInlineTags}
                >
                  <Save className="h-3 w-3 mr-1" />
                  Zapisz
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </TableCell>
      <TableCell>
        {variant.is_active ? (
          <Badge variant="default" className="bg-green-500">Aktywny</Badge>
        ) : (
          <Badge variant="secondary">Nieaktywny</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">{variant.impressions.toLocaleString()}</TableCell>
      <TableCell>
        <div className="space-y-1 text-right">
          <div className="font-medium">{variant.conversions} total</div>
          <div className="text-xs text-blue-600">
            📋 {variant.quiz_only_conversions} quiz ({variant.quiz_only_conversion_rate.toFixed(2)}%)
          </div>
          <div className="text-xs text-green-600">
            ✅ {variant.lead_conversions} lead ({variant.lead_conversion_rate.toFixed(2)}%)
          </div>
          <div className="text-xs text-purple-600">
            🖱️ {variant.cta_clicks} klik ({variant.cta_click_rate.toFixed(2)}%)
          </div>
        </div>
      </TableCell>
      <TableCell className="text-right font-medium">
        {variant.conversion_rate.toFixed(2)}%
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          {fullMetaUtm && (
            <div className="flex items-center gap-1">
              <Badge variant="default" className="text-[10px] bg-blue-600">PEŁNY</Badge>
              <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded max-w-[300px] truncate">
                {fullMetaUtm}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyLink(fullMetaUtm, "pełny UTM Meta")}
                className="h-6 w-6 p-0"
                title="Kopiuj pełny szablon Meta Ads"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          )}
          {utmContentUrl && (
            <div className="flex items-center gap-1">
              <Badge variant="default" className="text-[10px] bg-green-600">META</Badge>
              <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
                utm_content={primaryTag}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyLink(utmContentUrl, "Meta Ads (utm_content)")}
                className="h-6 w-6 p-0"
                title="Kopiuj dla Meta Ads"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          )}
          {pathBasedUrl && (
            <div className="flex items-center gap-1">
              <Badge variant="secondary" className="text-[10px]">PATH</Badge>
              <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
                /c/{primaryTag}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyLink(pathBasedUrl, "path-based")}
                className="h-6 w-6 p-0"
                title="Kopiuj link path-based"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          )}
          {!primaryTag && (
            <div className="flex items-center gap-1">
              <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
                v={shortId}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyLink(variantUrl, "ID wariantu")}
                className="h-6 w-6 p-0"
                title="Kopiuj link z ID"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(true)}
            title="Podgląd wariantu"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleActive(variant.id, variant.is_active)}
            title={variant.is_active ? "Dezaktywuj" : "Aktywuj"}
          >
            {variant.is_active ? (
              <ToggleRight className="h-4 w-4" />
            ) : (
              <ToggleLeft className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(variant.id)}
            title="Usuń"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>

    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Podgląd wariantu</span>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartEdit}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edytuj
              </Button>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? 'Edycja wariantu nagłówka' : 'Wszystkie wartości dla tego wariantu nagłówka'}
          </DialogDescription>
        </DialogHeader>
        
        {!isEditing ? (
          <div className="space-y-6 py-4">
            {/* Preview - jak będzie wyglądać na stronie */}
            <div className="gradient-hero p-8 rounded-lg">
              <div className="text-center space-y-4">
                <h1 className="text-3xl md:text-4xl font-bold text-white">
                  {variant.headline_text}
                </h1>
                <p className="text-lg text-white/90 max-w-xl mx-auto">
                  {variant.subtitle || 'Sprawdź w 2 minuty możliwości Twojej firmy do generowania sprzedaży, budowania wizerunku lidera branżowego  i otrzymaj spersonalizowane zalecenia dotyczące LinkedIn.'}
                </p>
                <Button className="bg-white text-primary hover:bg-white/90">
                  {variant.button_text || 'Rozpocznij quiz'}
                </Button>
              </div>
            </div>

            {/* Szczegóły tekstowe */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Nagłówek (Headline)</h4>
                <p className="text-sm p-3 bg-muted rounded-md">{variant.headline_text}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Podtytuł (Subtitle)</h4>
                <p className="text-sm p-3 bg-muted rounded-md">
                  {variant.subtitle || 'Sprawdź w 2 minuty możliwości Twojej firmy do generowania sprzedaży, budowania wizerunku lidera branżowego  i otrzymaj spersonalizowane zalecenia dotyczące LinkedIn.'}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Tekst buttona (CTA)</h4>
                <p className="text-sm p-3 bg-muted rounded-md">
                  {variant.button_text || 'Rozpocznij quiz'}
                </p>
              </div>

              {variant.notes && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Notatki</h4>
                  <p className="text-sm p-3 bg-muted rounded-md italic">{variant.notes}</p>
                </div>
              )}

              <div className="pt-4 border-t space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">Linki kampanii</h4>
                  
                  {utmContentUrl && (
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="default" className="bg-green-600">REKOMENDOWANY dla Meta Ads</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-3 py-2 rounded flex-1 font-mono break-all">
                          {utmContentUrl}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyLink(utmContentUrl, "Meta Ads (utm_content)")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {pathBasedUrl && (
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">BACKUP / QR kody</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-3 py-2 rounded flex-1 font-mono break-all">
                          {pathBasedUrl}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyLink(pathBasedUrl, "path-based")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {legacyTagUrl && (
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">LEGACY / testy</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-3 py-2 rounded flex-1 font-mono break-all">
                          {legacyTagUrl}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyLink(legacyTagUrl, "legacy (tag)")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {fullMetaUtm && (
                    <div className="space-y-2 pt-3 border-t">
                      <div className="flex items-center gap-2">
                        <Badge variant="default" className="bg-blue-600">PEŁNY SZABLON Meta Ads</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        Użyj tego w Meta Ads Manager - automatycznie podstawi nazwę kampanii
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-3 py-2 rounded flex-1 font-mono break-all">
                          {fullMetaUtm}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyLink(fullMetaUtm, "pełny UTM Meta")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Preview z edytowanymi wartościami */}
            <div className="gradient-hero p-8 rounded-lg">
              <div className="text-center space-y-4">
                <h1 className="text-3xl md:text-4xl font-bold text-white">
                  {editHeadline || 'Nagłówek...'}
                </h1>
                <p className="text-lg text-white/90 max-w-xl mx-auto">
                  {editSubtitle || 'Podtytuł...'}
                </p>
                <Button className="bg-white text-primary hover:bg-white/90">
                  {editButtonText || 'Tekst buttona...'}
                </Button>
              </div>
            </div>

            {/* Formularz edycji */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-headline">Nagłówek *</Label>
                <Input
                  id="edit-headline"
                  value={editHeadline}
                  onChange={(e) => setEditHeadline(e.target.value)}
                  placeholder="np. Czy Twoja firma jest gotowa na sprzedaż przez LinkedIn?"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-subtitle">Podtytuł</Label>
                <Textarea
                  id="edit-subtitle"
                  value={editSubtitle}
                  onChange={(e) => setEditSubtitle(e.target.value)}
                  placeholder="np. Sprawdź w 2 minuty możliwości Twojej firmy..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-buttonText">Tekst buttona</Label>
                <Input
                  id="edit-buttonText"
                  value={editButtonText}
                  onChange={(e) => setEditButtonText(e.target.value)}
                  placeholder="Rozpocznij quiz"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notatki (opcjonalne)</Label>
                <Textarea
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="np. Wariant skupiony na strachu przed stratą"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-tags">Tagi kampanii</Label>
                <div className="flex gap-2">
                  <Input
                    id="edit-tags"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="np. rachunkowość, kampania-linkedin"
                  />
                  <Button type="button" onClick={addTag} variant="outline" size="sm">
                    Dodaj
                  </Button>
                </div>
                {editTags.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {editTags.map(tag => (
                      <Badge key={tag} variant="secondary" className="gap-1">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 mr-2" />
                  Anuluj
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={isSaving || !editHeadline.trim()}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Zapisywanie...' : 'Zapisz'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};
